import {RecordingProcedure, TimelineMode} from "@/shared/timrTypes";
import {
	AbstractWorkingTimeRequestResponseModel,
	CombinedRecordingStatus,
	CustomFieldDefinition,
	Holiday,
	ProjectTime,
	RunningProjectTime,
	RunningWorkingTime,
	ValidationResult,
	WorkingTime,
	WorkingTimeDateSpanRequestResponseModel,
	WorkingTimeNotice,
	WorkingTimeNoticeRequestResponseModel,
	WorkingTimeRequestResponseModel,
	WorkingTimeType
} from "@/shared/timr";
import {Temporal} from "@/shared/utils/datefunctions";

export enum WorkingTimeTimelineItemType {
	RUNNING = "RUNNING",
	NONE = "NONE",
	WORKING_TIME = "WORKING_TIME",
	WORKING_TIME_REQUEST = "WORKING_TIME_REQUEST",
	GAP = "GAP",
	OVERLAP = "OVERLAP"
}

export interface WorkingTimeTimelineItem {
	date: string;
	type: WorkingTimeTimelineItemType;
	start: string;
	end: string;
	duration: number;
	workingTime?: WorkingTime;
	workingTimeRequest?: WorkingTimeRequestResponseModel;
	runningWorkingTime?: RunningWorkingTime;
	subitems?: ProjectTimeTimelineItem[];
	validationResults: ValidationResult[];
}

export interface WorkingTimeTimelineStatistics {
	workingTimesDurationInMilliseconds: number;
	breakTimesDurationInMinutes: number;
	gapsDurationInMilliseconds: number;
	projectTimesDurationInMilliseconds: number;
	projectTimeBreakTimesDurationInMinutes: number;
	projectTimeGapsDurationInMilliseconds: number;
}

export interface ProjectTimeTimelineDayStatistics {
	projectTimesDurationInMilliseconds: number;
	billableProjectTimesDurationInMilliseconds: number;
	notBillableProjectTimesDurationInMilliseconds: number;
	projectTimeBreakTimesDurationInMinutes: number;
	projectTimeGapsDurationInMilliseconds: number;
}

export interface ProjectTimeTimelineDayContent {
	date: string;
	items: ProjectTimeTimelineItem[];
	validationResults: ValidationResult[];
	holiday: Holiday;
	statistics: ProjectTimeTimelineDayStatistics;
	showTimeZone: boolean;
}

export interface CombinedTimelineDayComponentConfig {
	customFieldDefinitionsWorkingTime: CustomFieldDefinition[];
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	workingTimeTypes: WorkingTimeType[];
	recordingFunctionsActive: boolean;
	activeRecordingProcedure: RecordingProcedure;
	reportingOtherUsers: boolean;
	workingTimeTypesForNotices: WorkingTimeType[];
	workingTimeStatusNumbers: number[];
	projectTimeStatusNumbers: number[];
	mode: TimelineMode;
	projectTimeAttachmentsEnabled: boolean;
	timelineSortDescending: boolean;
}

export interface WorkingTimeTimelineDayContent {
	date: string;
	holiday: Holiday;
	workingTimeRequests: WorkingTimeDateSpanRequestResponseModel[];
	workingTimeNotices: WorkingTimeNotice[];
	workingTimeNoticeRequests: WorkingTimeNoticeRequestResponseModel[];
	dayBasedWorkingTimes: WorkingTime[];
	items: WorkingTimeTimelineItem[];
	statistics: WorkingTimeTimelineStatistics;
	validationResults: ValidationResult[];
	showTimeZone: boolean; // indicates if time zone should be displayed on a timeline day (includes client.showTimeZone setting)
}

export interface WorkingTimeTimelineStatistics {
	workingTimesDurationInMilliseconds: number;
	breakTimesDurationInMinutes: number;
	gapsDurationInMilliseconds: number;
	projectTimesDurationInMilliseconds: number;
	projectTimeBreakTimesDurationInMinutes: number;
	projectTimeGapsDurationInMilliseconds: number;
}

export interface ProjectTimeTimelineItem {
	date: string;
	type: ProjectTimeTimelineItemType;
	start: string;
	end: string;
	duration: number;
	projectTime?: ProjectTime;
	runningProjectTime?: RunningProjectTime;
	validationResults: ValidationResult[];
}

export enum ProjectTimeTimelineItemType {
	PROJECT_TIME = "PROJECT_TIME",
	RUNNING = "RUNNING",
	GAP = "GAP",
	OVERLAP = "OVERLAP"
}

export interface TimelineSubitemsComponentConfig {
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	recordingFunctionsActive: boolean;
	projectTimeStatusNumbers: number[];
	mode: TimelineMode;
	projectTimeAttachmentsEnabled: boolean;
	timelineSortDescending: boolean;
}

export interface TimelineSubitemsContent {
	workingTimeItem: WorkingTimeTimelineItem;
	validationResults: ValidationResult[];
	subitems: ProjectTimeTimelineItem[];
	showTimeZone: boolean; // indicates if time zone should be displayed on a timeline day (includes client.showTimeZone setting)
}

export interface TimelineSubitemsElement {
	content: TimelineSubitemsContent;
	config: TimelineSubitemsComponentConfig;
	combinedRecordingStatus: CombinedRecordingStatus;
}

/**
 * Interface for add working time event (e.g. triggered by click on "+ item" / context menu in TimelineDay)
 */
export interface AddWorkingTimeEvent {
	workingTimeTypeFilter: WorkingTimeTypeFilter;
	start: Temporal.ZonedDateTime | null;
	end: Temporal.ZonedDateTime | null;
}

/**
 * Interface for quick switch working time event
 */
export interface QuickSwitchWorkingTimeEvent {
	quickSwitchProcedure: RecordingProcedure;
	workingTime: WorkingTime;
}

/**
 * Interface for quick switch project time event
 */
export interface QuickSwitchProjectTimeEvent {
	quickSwitchProcedure: RecordingProcedure;
	workingTime?: WorkingTime;
	projectTime: ProjectTime;
}

/**
 * Interface for add project time event (e.g. triggered by click on "+ item" in CombinedTimelineSubitems)
 */
export interface AddProjectTimeEvent {
	start: Temporal.ZonedDateTime | null;
	end: Temporal.ZonedDateTime | null;
}

/**
 * Interface for show working time event
 * e.g. triggered by click on 'Show' entry of a context menu in a working time item or by click on 'Show' entry of a context menu in working time item
 */
export interface ShowWorkingTimeEvent {
	workingTimeItem: WorkingTimeItem;
}

export interface EditWorkingTimeEvent {
	workingTimeItem: WorkingTimeItem;
}

/**
 * Interface for show project time event
 * e.g. triggered by click on 'Show' entry of a context menu in a project time subitem or by a click on 'Show' entry of a context menu in subitem
 */
export interface ShowProjectTimeEvent {
	projectTimeItem: ProjectTimeItem;
}

export interface EditProjectTimeEvent {
	projectTimeItem: ProjectTimeItem;
}

/**
 * Interface for duplicate project time event (e.g. triggered by click on 'Duplicate' entry of a context menu in subitem)
 */
export interface DuplicateProjectTimeEvent {
	projectTimeItem: ProjectTimeItem;
}

/**
 * Interface for show task details event (e.g. triggered by click on 'Show Task Details' entry of a context menu in subitem)
 */
export interface ShowTaskDetailsEvent {
	projectTime: ProjectTime | RunningProjectTime;
}

/**
 * Interface for status-updated event (e.g. triggered by click on 'Set to Changeable' entry of a context menu in item/subitem)
 */
export interface StatusUpdatedEvent {
	statusName: string;
}

/**
 * Interface for show working time request event (triggered by click on a request item in the timeline)
 */
export interface ShowWorkingTimeRequestEvent {
	workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
}

/**
 * Interface for show working time notice event (triggered by click on a notice item in the timeline)
 */
export interface ShowWorkingTimeNoticeEvent {
	workingTimeNotice: WorkingTimeNotice;
}

/**
 * Interface for show working time notice request event (triggered by click on a notice request item, or on a notice
 * created by a request, in the timeline)
 */
export interface ShowWorkingTimeNoticeRequestEvent {
	workingTimeRequestId: number;
}

/**
 * Interface for add working time notice event (triggered by the "add working time notice" entry of a timeline day context menu)
 */
export interface AddWorkingTimeNoticeEvent {
	start: Temporal.PlainDate | null;
	end: Temporal.PlainDate | null;
}

/**
 * Interface for edit working time request event (triggered by context menu in a request item)
 */
export interface EditWorkingTimeRequestEvent {
	workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
}

/**
 * Interface for duplicate working time request event (triggered by context menu in a request item)
 */
export interface DuplicateWorkingTimeRequestEvent {
	workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
}

/**
 * Interface for duplicate working time event (e.g. triggered by click on a working time item in subitem)
 */
export interface DuplicateWorkingTimeEvent {
	workingTimeItem: WorkingTimeItem;
}

/**
 * Type for function to filter working time types<p>
 * Used to filter types for add absence/vacation or working time dialog
 */
export type WorkingTimeTypeFilter = (workingTimeTypes: WorkingTimeType[]) => WorkingTimeType[];

export interface WorkingTimeItem {
	workingTime: WorkingTime;
	validationResults?: ValidationResult[]; // only available for minute based working times
}

export interface ProjectTimeItem {
	projectTime: ProjectTime;
	validationResults: ValidationResult[];
}

export interface TimelineEntries {
	combinedRecordingStatus: CombinedRecordingStatus;
	workingTimeTimelineDays: WorkingTimeTimelineDayContent[] | null;
	projectTimeTimelineDays: ProjectTimeTimelineDayContent[] | null;
}
